import type { EnvJson } from "../../context/internal/useFetchEnvJson";

import type { WaterlyAppStateCreator } from "./waterlyAppStateCreator.type";

type EnvironmentDetails = {
  BuildId: string;
  CommitSHA: string;
  Deployment: EnvJson["deployment"] | "pending";
  Release: string;
  AnalyticsId: "unknown" | string;
  AppInsightsConnectionString?: string;
  MixpanelToken: "unknown" | string;
  MixpanelEnableSuperadminTracking: boolean;
  MixpanelRecordSessionsPercent: number;
};

type EnvState = {
  env: EnvironmentDetails;
  isNewReleaseAvailable: boolean;
};

type EnvActions = {
  setEnv: (newEnv: EnvironmentDetails) => void;
  setIsNewReleaseAvailable: (isNewReleaseAvailable: boolean) => void;
};

const initialState: EnvState = {
  env: {
    BuildId: process.env.NX_BUILD_ID ?? "unknown",
    CommitSHA: process.env.NX_BUILD_SHA ?? "unknown",
    Deployment: "pending",
    Release: "pending",
    AnalyticsId: "unknown",
    MixpanelToken: "unknown",
    MixpanelEnableSuperadminTracking: false,
    MixpanelRecordSessionsPercent: 0,
  },
  isNewReleaseAvailable: false,
};

const isSameEnv = (left: EnvironmentDetails, right: EnvironmentDetails) =>
  left.AnalyticsId === right.AnalyticsId &&
  left.AppInsightsConnectionString === right.AppInsightsConnectionString &&
  left.BuildId === right.BuildId &&
  left.CommitSHA === right.CommitSHA &&
  left.Deployment === right.Deployment &&
  left.MixpanelEnableSuperadminTracking === right.MixpanelEnableSuperadminTracking &&
  left.MixpanelRecordSessionsPercent === right.MixpanelRecordSessionsPercent &&
  left.MixpanelToken === right.MixpanelToken &&
  left.Release === right.Release;

export const createEnvSlice: WaterlyAppStateCreator<EnvActions, EnvState> = (set) => ({
  ...initialState,
  setEnv: (newEnv) =>
    set((state) => (isSameEnv(state.env, newEnv) ? state : { env: newEnv }), undefined, "env:setEnv"),
  setIsNewReleaseAvailable: (isNewReleaseAvailable) =>
    set(
      (state) => (state.isNewReleaseAvailable === isNewReleaseAvailable ? state : { isNewReleaseAvailable }),
      undefined,
      "env:setIsNewReleaseAvailable",
    ),
});
